/**
 * Trunnion AI - Node Inspector
 * Right panel for editing the selected node's properties.
 */

import { X, Trash2 } from 'lucide-react';
import { useWorkflowDesignerStore } from '@/stores/workflowDesignerStore';
import { AgentConfigForm } from './AgentConfigForm';
import { ClearanceSelector } from './ClearanceSelector';
import { Input } from '@/components/ui/Input';
import { Select } from '@/components/ui/Select';
import { Button } from '@/components/ui/Button';
import type {
  AgentNodeData,
  HumanGateNodeData,
  ConditionalNodeData,
  TriggerNodeData,
  TerminalNodeData,
  ParallelNodeData,
} from '@/types';

export function NodeInspector() {
  const selectedNodeId = useWorkflowDesignerStore((s) => s.selectedNodeId);
  const nodes = useWorkflowDesignerStore((s) => s.nodes);
  const updateNodeData = useWorkflowDesignerStore((s) => s.updateNodeData);
  const removeNode = useWorkflowDesignerStore((s) => s.removeNode);
  const clearSelection = useWorkflowDesignerStore((s) => s.clearSelection);

  const selectedNode = nodes.find((n) => n.id === selectedNodeId);

  if (!selectedNode) {
    return (
      <div className="flex flex-col h-full w-72 border-l border-trunnion-slate-700 bg-trunnion-slate-900">
        <div className="flex-1 flex items-center justify-center p-6 text-center">
          <p className="text-sm text-trunnion-slate-400">
            Select a node to view its properties
          </p>
        </div>
      </div>
    );
  }

  const data = selectedNode.data;
  const nodeType = (data as { nodeType?: string }).nodeType ?? selectedNode.type;

  return (
    <div className="flex flex-col h-full w-72 border-l border-trunnion-slate-700 bg-trunnion-slate-900">
      {/* Header */}
      <div className="flex items-center justify-between border-b border-trunnion-slate-700 px-4 py-3">
        <div>
          <h3 className="text-sm font-semibold text-white capitalize">
            {nodeType} Node
          </h3>
          <p className="text-xs text-trunnion-slate-400">ID: {selectedNode.id}</p>
        </div>
        <button
          onClick={clearSelection}
          className="rounded p-1 text-trunnion-slate-400 hover:bg-trunnion-slate-800 hover:text-white"
        >
          <X className="h-4 w-4" />
        </button>
      </div>

      {/* Form Content */}
      <div className="flex-1 overflow-y-auto px-4 py-4 space-y-4">
        {nodeType === 'agent' && (
          <AgentConfigForm
            nodeId={selectedNode.id}
            data={data as AgentNodeData}
          />
        )}

        {nodeType === 'humanGate' && (
          <HumanGateForm
            nodeId={selectedNode.id}
            data={data as HumanGateNodeData}
            updateNodeData={updateNodeData}
          />
        )}

        {nodeType === 'conditional' && (
          <ConditionalForm
            nodeId={selectedNode.id}
            data={data as ConditionalNodeData}
            updateNodeData={updateNodeData}
          />
        )}

        {nodeType === 'trigger' && (
          <TriggerForm
            nodeId={selectedNode.id}
            data={data as TriggerNodeData}
            updateNodeData={updateNodeData}
          />
        )}

        {nodeType === 'terminal' && (
          <TerminalForm
            nodeId={selectedNode.id}
            data={data as TerminalNodeData}
            updateNodeData={updateNodeData}
          />
        )}

        {(nodeType === 'parallelFork' || nodeType === 'parallelJoin') && (
          <ParallelForm
            nodeId={selectedNode.id}
            data={data as ParallelNodeData}
            updateNodeData={updateNodeData}
          />
        )}
      </div>

      {/* Delete button */}
      <div className="border-t border-trunnion-slate-700 p-4">
        <Button
          variant="danger"
          size="sm"
          icon={<Trash2 className="h-4 w-4" />}
          className="w-full"
          onClick={() => {
            removeNode(selectedNode.id);
          }}
        >
          Delete Node
        </Button>
      </div>
    </div>
  );
}

// --- Sub-forms ---

function HumanGateForm({
  nodeId,
  data,
  updateNodeData,
}: {
  nodeId: string;
  data: HumanGateNodeData;
  updateNodeData: (id: string, d: Partial<HumanGateNodeData>) => void;
}) {
  return (
    <div className="space-y-4">
      <Input
        label="Label"
        value={data.label}
        onChange={(e) => updateNodeData(nodeId, { label: e.target.value })}
      />
      <Input
        label="Description"
        value={data.description}
        onChange={(e) => updateNodeData(nodeId, { description: e.target.value })}
      />
      <Input
        label="Timeout (minutes)"
        type="number"
        value={data.timeoutMinutes}
        onChange={(e) =>
          updateNodeData(nodeId, {
            timeoutMinutes: parseInt(e.target.value) || 60,
          })
        }
      />
      <ClearanceSelector
        value={data.classification}
        onChange={(classification) => updateNodeData(nodeId, { classification })}
      />
      <div className="flex items-center gap-2">
        <input
          type="checkbox"
          id="requires-justification"
          checked={data.requiresJustification}
          onChange={(e) =>
            updateNodeData(nodeId, { requiresJustification: e.target.checked })
          }
          className="rounded border-trunnion-slate-600"
        />
        <label
          htmlFor="requires-justification"
          className="text-sm text-trunnion-slate-200"
        >
          Requires justification
        </label>
      </div>
    </div>
  );
}

function ConditionalForm({
  nodeId,
  data,
  updateNodeData,
}: {
  nodeId: string;
  data: ConditionalNodeData;
  updateNodeData: (id: string, d: Partial<ConditionalNodeData>) => void;
}) {
  return (
    <div className="space-y-4">
      <Input
        label="Label"
        value={data.label}
        onChange={(e) => updateNodeData(nodeId, { label: e.target.value })}
      />
      <Select
        label="Condition Type"
        value={data.conditionType}
        onChange={(e) =>
          updateNodeData(nodeId, {
            conditionType: e.target.value as ConditionalNodeData['conditionType'],
          })
        }
        options={[
          { value: 'expression', label: 'Expression' },
          { value: 'llm_eval', label: 'LLM Evaluation' },
          { value: 'threshold', label: 'Threshold' },
        ]}
      />
      <div>
        <label className="text-sm font-medium text-trunnion-slate-200">
          Condition
        </label>
        <textarea
          value={data.condition}
          onChange={(e) => updateNodeData(nodeId, { condition: e.target.value })}
          rows={3}
          className="mt-1.5 w-full rounded-lg border border-trunnion-slate-600 bg-trunnion-slate-800 px-3 py-2 text-sm text-white font-mono placeholder-trunnion-slate-400 focus:outline-none focus:ring-2 focus:ring-trunnion-gold-500 resize-none"
          placeholder="e.g. result.score > 0.8"
        />
      </div>
    </div>
  );
}

function TriggerForm({
  nodeId,
  data,
  updateNodeData,
}: {
  nodeId: string;
  data: TriggerNodeData;
  updateNodeData: (id: string, d: Partial<TriggerNodeData>) => void;
}) {
  return (
    <div className="space-y-4">
      <Input
        label="Label"
        value={data.label}
        onChange={(e) => updateNodeData(nodeId, { label: e.target.value })}
      />
      <Select
        label="Trigger Type"
        value={data.triggerType}
        onChange={(e) =>
          updateNodeData(nodeId, {
            triggerType: e.target.value as TriggerNodeData['triggerType'],
          })
        }
        options={[
          { value: 'manual', label: 'Manual' },
          { value: 'scheduled', label: 'Scheduled' },
          { value: 'webhook', label: 'Webhook' },
          { value: 'event', label: 'Event' },
        ]}
      />
      {data.triggerType === 'scheduled' && (
        <Input
          label="Schedule (cron)"
          value={data.schedule ?? ''}
          onChange={(e) => updateNodeData(nodeId, { schedule: e.target.value })}
          placeholder="0 */6 * * *"
        />
      )}
      {data.triggerType === 'webhook' && (
        <Input
          label="Webhook Path"
          value={data.webhookPath ?? ''}
          onChange={(e) =>
            updateNodeData(nodeId, { webhookPath: e.target.value })
          }
          placeholder="/webhooks/my-trigger"
        />
      )}
    </div>
  );
}

function TerminalForm({
  nodeId,
  data,
  updateNodeData,
}: {
  nodeId: string;
  data: TerminalNodeData;
  updateNodeData: (id: string, d: Partial<TerminalNodeData>) => void;
}) {
  return (
    <div className="space-y-4">
      <Input
        label="Label"
        value={data.label}
        onChange={(e) => updateNodeData(nodeId, { label: e.target.value })}
      />
      <Select
        label="Terminal Type"
        value={data.terminalType}
        onChange={(e) =>
          updateNodeData(nodeId, {
            terminalType: e.target.value as TerminalNodeData['terminalType'],
          })
        }
        options={[
          { value: 'success', label: 'Success' },
          { value: 'failure', label: 'Failure' },
          { value: 'cancelled', label: 'Cancelled' },
        ]}
      />
      <div className="flex items-center gap-2">
        <input
          type="checkbox"
          id="notify-complete"
          checked={data.notifyOnComplete}
          onChange={(e) =>
            updateNodeData(nodeId, { notifyOnComplete: e.target.checked })
          }
          className="rounded border-trunnion-slate-600"
        />
        <label
          htmlFor="notify-complete"
          className="text-sm text-trunnion-slate-200"
        >
          Notify on complete
        </label>
      </div>
    </div>
  );
}

function ParallelForm({
  nodeId,
  data,
  updateNodeData,
}: {
  nodeId: string;
  data: ParallelNodeData;
  updateNodeData: (id: string, d: Partial<ParallelNodeData>) => void;
}) {
  return (
    <div className="space-y-4">
      <Input
        label="Label"
        value={data.label}
        onChange={(e) => updateNodeData(nodeId, { label: e.target.value })}
      />
      <Select
        label="Strategy"
        value={data.strategy ?? 'all'}
        onChange={(e) =>
          updateNodeData(nodeId, {
            strategy: e.target.value as ParallelNodeData['strategy'],
          })
        }
        options={[
          { value: 'all', label: 'All (wait for all)' },
          { value: 'any', label: 'Any (first to complete)' },
          { value: 'majority', label: 'Majority' },
        ]}
      />
      <Input
        label="Timeout (seconds)"
        type="number"
        value={data.timeoutSeconds ?? 600}
        onChange={(e) =>
          updateNodeData(nodeId, {
            timeoutSeconds: parseInt(e.target.value) || 600,
          })
        }
      />
    </div>
  );
}
